import {
  SOCOTEC_GROUPS,
  SOCOTEC_EMPLOYEE_GROUPS,
  MANAGER_GROUPS,
  EXTRA_MANAGER_GROUPS,
  NO_ACCESS_GROUPS,
} from "@/utils/users";
import { getInitials } from "@/utils/formatting";

export default class UserProfile {
  constructor(userProfile) {
    this.setData(userProfile);
  }

  setData(userProfile) {
    if (!userProfile) {
      userProfile = {};
    }
    this.id = userProfile.id || "";
    this.email = userProfile.email || "";
    this.first_name = userProfile.firstName ?? userProfile.first_name ?? "";
    this.last_name = userProfile.lastName ?? userProfile.last_name ?? "";
    this.user_initials = userProfile.user_initials || "";
    this.user_full_name = userProfile.user_full_name || "";
    this.telephone_number = userProfile.telephoneNumber ?? userProfile.telephone_number ?? "";
    this.job_title = userProfile.jobTitle ?? userProfile.job_title ?? "";
    this.signature = userProfile.signature || "";
    this.is_superuser = userProfile.isSuperuser ?? userProfile.is_superuser ?? false;
    this.language = userProfile.language ?? "";
    this.is_multi_language_activated =
      userProfile.isMultiLanguageActivated ?? userProfile.is_multi_language_activated ?? false;
    this.iris_access_token = userProfile.irisAccessToken ?? userProfile.iris_access_token ?? "";

    // Backend Api could send agency object or agency_uuid field
    if (!userProfile.agency) {
      userProfile.agency = {
        uuid: userProfile.agency_uuid || null,
      };
    }
    // Backend Api could send agency.agency_group object or agency_group_uuid field
    if (!userProfile.agency.agency_group) {
      userProfile.agency.agency_group = userProfile.agency.agencyGroup ?? {
        uuid: userProfile.agency_group_uuid || null,
      };
    }

    this.agency = {
      uuid: userProfile.agency.uuid || null,
      name: userProfile.agency.name || "",
      country: userProfile.agency.country || "",
      address: userProfile.agency.address || "",
      city: userProfile.agency.city || "",
      agency_group: {
        uuid: userProfile.agency.agency_group.uuid || null,
        name: userProfile.agency.agency_group.name || "",
      },
    };
    this.groups = userProfile.groups || [];
    // List of agency_codes user managed (from SSO)
    this.managed_agency_codes =
      userProfile.managedAgencyCodes ?? userProfile.managed_agency_codes ?? [];
    this.business_line = this.groups.join(", ");

    this.full_name = `${this.first_name} ${this.last_name}`;
    this.usermanagement_uuid =
      userProfile.usermanagementUuid ?? userProfile.usermanagement_uuid ?? "";
  }

  getNameOrEmail() {
    if (this.first_name) {
      return `${this.first_name} ${this.last_name}`;
    }
    return this.email;
  }

  getNameFromEmail() {
    let name;
    try {
      const [local, domain] = this.email.split("@");
      name = `${local} ${domain.split(".")[0]}`;
    } catch (e) {
      console.error(e);
      name = this.email;
    }
    return name;
  }

  getInitials() {
    return getInitials(this.first_name, this.last_name);
  }

  getFullName() {
    return `${this.first_name} ${this.last_name}`;
  }

  isDev() {
    return this.groups.includes(SOCOTEC_GROUPS.DEVELOPER);
  }

  isFromSocotec() {
    return this.groups.some((group) => SOCOTEC_EMPLOYEE_GROUPS.includes(group));
  }

  isExternal() {
    return (
      this.groups.some((group) => NO_ACCESS_GROUPS.includes(group)) || this.groups.length === 0
    );
  }

  isAgencyManager() {
    return this.groups.includes(SOCOTEC_GROUPS.AGENCY_MANAGER);
  }

  isActivityDirector() {
    return this.groups.includes(SOCOTEC_GROUPS.ACTIVITY_DIRECTOR);
  }

  isAGKeyUser() {
    return this.groups.includes(SOCOTEC_GROUPS.AG_KEY_USER);
  }

  isKeyUser() {
    return this.groups.includes(SOCOTEC_GROUPS.KEY_USER);
  }

  isAgencyGroupManager() {
    return this.groups.includes(SOCOTEC_GROUPS.AGENCY_GROUP_MANAGER);
  }

  isNationalManager() {
    return this.groups.includes(SOCOTEC_GROUPS.NATIONAL_MANAGER);
  }

  isTechnicalDirection() {
    return this.groups.includes(SOCOTEC_GROUPS.TECHNICAL_DIRECTION);
  }

  isAgencyGroupManagerOrNationalManager() {
    return this.isAgencyGroupManager() || this.isNationalManager();
  }

  isHierarchicalManager() {
    return this.groups.some((group) => MANAGER_GROUPS.includes(group));
  }

  isExtraManager() {
    return this.groups.some((group) => EXTRA_MANAGER_GROUPS.includes(group));
  }

  isHierarchicalOrExtraManager() {
    return this.isHierarchicalManager() || this.isExtraManager();
  }

  isDevOrSuperUser() {
    if (this.is_superuser === true || this.isDev() === true) {
      return true;
    }
    return false;
  }

  isProjectManagerOrAbove(project) {
    if (!project) {
      return false;
    }
    return this.isItemManagerorAbove(project.projectManager, project.contractingAgency?.code);
  }

  isItemManagerorAbove(itemManager, agencyCode) {
    if (this.isDevOrSuperUser() || this.isNationalManager() || this.isTechnicalDirection()) {
      return true;
    }
    if (itemManager) {
      if (itemManager.id === this.id) {
        return true;
      }
    }
    if (!agencyCode) {
      return false;
    }
    if (this.isHierarchicalOrExtraManager() && this.managed_agency_codes.includes(agencyCode)) {
      return true;
    }
    return false;
  }
}
